import {
  CopyIndustryTemplatePagesRequest,
  GetIndustryTemplateHintsRequest,
  GetIndustryTemplateRequest,
  GetTemplatePagesV2Request,
  GetTemplatePagesWithRuleRequest,
  GetTemplatePagesWithRuleResponse,
  InformationFile,
  Template,
  TemplateHint,
  TemplatePage,
  TemplatesApi,
} from '@ulysses-inc/harami_api_client'
import { normalize } from 'normalizr'
import { all, call, put, takeEvery } from 'redux-saga/effects'
import * as actions from 'src/state/ducks/templates/actions'
import { setRepeatSerialNumbers } from 'src/state/ducks/templates/editSerialNumber/actions'
import * as editTemplateActions from 'src/state/ducks/templates/editTemplate/actions'
import { editTemplateSagas } from 'src/state/ducks/templates/editTemplate/sagas'
import { ActionTypes as ActionTypesOfEditTemplate } from 'src/state/ducks/templates/editTemplate/types'
import * as templateHintsActions from 'src/state/ducks/templates/templateHints/actions'
import { templateHintsSagas } from 'src/state/ducks/templates/templateHints/sagas'
import BaseClient from '../../middleware/saga/baseClient'
import {
  HTTPError,
  handleHTTPError,
  isHTTPError,
} from '../../middleware/saga/handleHttpError'
import {
  S3UploadResult,
  addInformationFilesToApiServer,
  compressFiles,
  uploadFilesToS3UsingPresignedURL,
} from '../files/saga'
import interceptionsActions from '../interceptions/actions'
import {
  SchemaTemplatePages,
  SchemaTemplatePagesWithRule,
  TemplatePagesEntities,
  TemplatePagesWithRuleEntities,
  TemplatePagesWithRuleEntitiesNormalizeResultType,
} from './schemas'
import { ActionTypes } from './types'

const baseClient = new BaseClient()

const getTemplatePagesV2Request = (req: GetTemplatePagesV2Request) => {
  return baseClient
    .getApi(TemplatesApi)
    .getTemplatePagesV2(req)
    .then(templatePages => templatePages)
    .catch(handleHTTPError)
}

const getTemplatePagesWithRuleRequest = (
  req: GetTemplatePagesWithRuleRequest,
) => {
  return baseClient
    .getApi(TemplatesApi)
    .getTemplatePagesWithRule(req)
    .then(pages => pages)
    .catch(handleHTTPError)
}

const getIndustryTemplateRequest = (req: GetIndustryTemplateRequest) => {
  return baseClient
    .getApi(TemplatesApi)
    .getIndustryTemplate(req)
    .then(template => template)
    .catch(handleHTTPError)
}

const getIndustryTemplateHintsRequest = (
  req: GetIndustryTemplateHintsRequest,
) => {
  return baseClient
    .getApi(TemplatesApi)
    .getIndustryTemplateHints(req)
    .then(templateHints => templateHints)
    .catch(handleHTTPError)
}

const copyIndustryTemplatePagesRequest = (
  req: CopyIndustryTemplatePagesRequest,
) => {
  return baseClient
    .getApi(TemplatesApi)
    .copyIndustryTemplatePages(req)
    .then(templatePages => templatePages)
    .catch(handleHTTPError)
}

function* getTemplatePages(
  action: ReturnType<typeof actions.getTemplatePages>,
) {
  try {
    const params: GetTemplatePagesV2Request = {
      templateId: action.templateId,
      templateFilter: { placeNodeId: { $in: [action.loginPlaceNode?.uuid] } },
    }

    const templatePages: TemplatePage[] = yield call(
      getTemplatePagesV2Request,
      params,
    )

    const normalized = normalize<any, TemplatePagesEntities, Array<number>>(
      templatePages,
      SchemaTemplatePages,
    )
    yield put(
      actions.getSuccessTemplatePages(
        normalized.entities.templatePages,
        normalized.entities.nodes,
        normalized.result,
      ),
    )
    yield put(setRepeatSerialNumbers(Object.values(normalized.entities.nodes)))
  } catch (error) {
    yield put(interceptionsActions.handleHttpError(error as HTTPError))
    yield put(actions.getErrorTemplatePages(error as Error))
  }
}

function* getTemplatePagesWithRule(
  action: ReturnType<typeof actions.getTemplatePagesWithRule>,
) {
  try {
    const params: GetTemplatePagesWithRuleRequest = {
      templateId: action.templateId,
      templateFilter: { placeNodeId: { $in: [action.loginPlaceNode?.uuid] } },
    }

    const response: GetTemplatePagesWithRuleResponse = yield call(
      getTemplatePagesWithRuleRequest,
      params,
    )

    const normalized = normalize<
      GetTemplatePagesWithRuleResponse,
      TemplatePagesWithRuleEntities,
      TemplatePagesWithRuleEntitiesNormalizeResultType
    >(response, SchemaTemplatePagesWithRule)

    const isReportNamingRulesEnabled = response.rule !== undefined
    const templateReportNamingRuleNodeUuid =
      response.rule && response.rule.items[0]
        ? response.rule.items[0].templateNodeUuid
        : ''

    yield put(
      actions.getSuccessTemplatePagesWithRule(
        normalized.entities.pages,
        normalized.entities.nodes,
        normalized.result.pages,
        isReportNamingRulesEnabled,
        templateReportNamingRuleNodeUuid,
        response.masterUuid ?? null,
      ),
    )
    yield put(setRepeatSerialNumbers(Object.values(normalized.entities.nodes)))
  } catch (error) {
    yield put(interceptionsActions.handleHttpError(error as HTTPError))
    yield put(actions.getErrorTemplatePages(error as Error))
  }
}

function* uploadInformationFiles(
  action: ReturnType<typeof actions.uploadInformationFiles>,
) {
  try {
    // ファイルの圧縮
    const compressedFiles: File[] = yield call(compressFiles, action.files)

    // S3へアップロード
    const s3UploadResults: S3UploadResult[] = yield call(
      uploadFilesToS3UsingPresignedURL,
      compressedFiles,
    )

    // S3へアップロードしたファイルの情報をAPIサーバに登録
    const informationFiles: InformationFile[] = yield call(
      addInformationFilesToApiServer,
      s3UploadResults,
    )

    yield put(
      actions.uploadSuccessInformationFiles(action.nodeId, informationFiles),
    )
  } catch (error) {
    if (isHTTPError(error)) {
      // APIサーバーからのエラーレスポンスのハンドリング
      yield put(interceptionsActions.handleHttpError(error as HTTPError))
    } else {
      // それ以外のエラーのハンドリング
      // 現状UIに表示する機構がないため、とりあえずログに出力しておく
      console.error(error)
    }

    yield put(actions.uploadErrorInformationFiles(error as Error))
  }
}

function* getPreparedTemplate(
  action: ReturnType<typeof editTemplateActions.getPreparedTemplate>,
) {
  try {
    const [template, templateHints, templatePages]: [
      Template,
      TemplateHint[],
      TemplatePage[],
    ] = yield all([
      call(getIndustryTemplateRequest, {
        templateId: action.templateId,
        key: action.key,
      }),
      call(getIndustryTemplateHintsRequest, {
        templateId: action.templateId,
        key: action.key,
      }),
      call(copyIndustryTemplatePagesRequest, {
        templateId: action.templateId,
        key: action.key,
      }),
    ])

    yield put(editTemplateActions.getSuccessTemplate(template))
    yield put(templateHintsActions.getSuccessTemplateHints(templateHints))
    const normalized = normalize<any, TemplatePagesEntities, Array<number>>(
      templatePages,
      SchemaTemplatePages,
    )
    yield put(
      actions.getSuccessTemplatePages(
        normalized.entities.templatePages,
        normalized.entities.nodes,
        normalized.result,
      ),
    )
  } catch (error) {
    yield put(interceptionsActions.handleHttpError(error as HTTPError))
  }
}

function* copyTemplatePage(
  action: ReturnType<typeof actions.startCopyTemplatePage>,
) {
  // 同期処理で loading の表示/非表示の state を切り替えるために nextTick 関数を使用している。
  // 例えば、isLoading: true -> isLoading: false のように state を遷移させても、
  // 一連の処理が同期的に行われると isLoading: false として render が行われるため isLoading: true では render されない。
  // nextTick 関数を挟むことにより、isLoading: true で render され、isLoading: false でも render される。
  yield call(nextTick)
  yield put(actions.copyTemplatePage(action.targetPageId))
}

const nextTick = () => {
  return new Promise(resolve => setTimeout(resolve, 0))
}

const sagas = [
  // 以下のコードには到達し得ない。所要のコード修正がされるまで一旦コメントアウトする。
  // takeEvery(ActionTypes.REQUEST_ADD_TEMPLATE_PAGES, addTemplatePages),
  takeEvery(ActionTypes.REQUEST_GET_TEMPLATE_PAGES, getTemplatePages),
  takeEvery(
    ActionTypes.REQUEST_GET_TEMPLATE_PAGES_WITH_RULE,
    getTemplatePagesWithRule,
  ),
  takeEvery(
    ActionTypes.REQUEST_UPLOAD_INFORMATION_FILES,
    uploadInformationFiles,
  ),
  takeEvery(
    ActionTypesOfEditTemplate.REQUEST_GET_PREPARED_TEMPLATE,
    getPreparedTemplate,
  ),
  takeEvery(ActionTypes.START_COPY_TEMPLATE_PAGE, copyTemplatePage),
  ...editTemplateSagas,
  ...templateHintsSagas,
]

export default sagas
